import { useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { CalendarIcon } from '../Icons/CalendarIcon'

type SelectedDate = Date | null

interface Props {
    sendDate: (data: { start: Date | null; end: Date | null }) => void
}

export default function DateRangePicker({ sendDate }: Props) {
    const [startDate, setStartDate] = useState<SelectedDate>(null)
    const [endDate, setEndDate] = useState<SelectedDate>(null)
    const [showStartPicker, setShowStartPicker] = useState(false)
    const [showEndPicker, setShowEndPicker] = useState(false)
    const [currentMonth, setCurrentMonth] = useState(new Date())
    const startPickerRef = useRef<HTMLDivElement>(null)
    const endPickerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                startPickerRef.current &&
                !startPickerRef.current.contains(event.target as Node)
            ) {
                setShowStartPicker(false)
            }
            if (
                endPickerRef.current &&
                !endPickerRef.current.contains(event.target as Node)
            ) {
                setShowEndPicker(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleStartDateChange = (date: Date) => {
        setStartDate(date)
        setShowStartPicker(false)
        sendDate({ start: date, end: endDate })
    }

    const handleEndDateChange = (date: Date) => {
        if (startDate && date < startDate) return
        setEndDate(date)
        setShowEndPicker(false)
        sendDate({ start: startDate, end: date })
    }

    const formatDate = (date: SelectedDate) => {
        if (!date) return ''
        return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`
    }

    const getDaysInMonth = (date: Date) => {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
    }

    const getFirstDayOfMonth = (date: Date) => {
        return new Date(date.getFullYear(), date.getMonth(), 1).getDay()
    }

    const previousMonth = () => {
        setCurrentMonth(
            new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
        )
    }

    const nextMonth = () => {
        setCurrentMonth(
            new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
        )
    }

    const renderCalendar = (onSelectDate: (date: Date) => void) => {
        const daysInMonth = getDaysInMonth(currentMonth)
        const firstDay = getFirstDayOfMonth(currentMonth)
        const days = []
        const weekDays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sab']

        // Encabezados de días de la semana
        const headerDays = weekDays.map((day) => (
            <div
                key={day}
                className="text-center text-xs font-semibold text-gray-600 py-2"
            >
                {day}
            </div>
        ))

        // Celdas vacías del mes anterior
        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="p-2"></div>)
        }

        // Días del mes
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(
                currentMonth.getFullYear(),
                currentMonth.getMonth(),
                day
            )
            const isSelected =
                startDate && date.toDateString() === startDate.toDateString()
            const isEndSelected =
                endDate && date.toDateString() === endDate.toDateString()
            const isInRange =
                startDate && endDate && date > startDate && date < endDate

            days.push(
                <button
                    key={day}
                    onClick={() => onSelectDate(date)}
                    className={`p-2 text-sm rounded-lg font-medium transition-colors ${
                        isSelected || isEndSelected
                            ? 'bg-green-600 text-white'
                            : isInRange
                              ? 'bg-green-100 text-gray-900'
                              : 'text-gray-900 hover:bg-gray-100'
                    }`}
                >
                    {day}
                </button>
            )
        }

        return (
            <div className="w-80 bg-white rounded-lg shadow-lg border border-gray-200 p-4">
                {/* Encabezado del calendario */}
                <div className="flex items-center justify-between mb-4">
                    <button
                        onClick={previousMonth}
                        className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ChevronLeft size={20} className="text-gray-600" />
                    </button>
                    <h3 className="text-sm font-semibold text-gray-900">
                        {currentMonth.toLocaleDateString('es-ES', {
                            month: 'long',
                            year: 'numeric',
                        })}
                    </h3>
                    <button
                        onClick={nextMonth}
                        className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ChevronRight size={20} className="text-gray-600" />
                    </button>
                </div>

                {/* Grid de días */}
                <div className="grid grid-cols-7 gap-1">
                    {headerDays}
                    {days}
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-row items-center justify-center w-[234px] h-[36px] rounded-lg border border-gray-300 px-3 py-2 gap-2 bg-white box-border">
            <CalendarIcon className="shrink-0" />

            {/* Campo de fecha inicial */}
            <div
                className="relative flex-1"
                ref={startPickerRef}
            >
                <button
                    onClick={() => setShowStartPicker(!showStartPicker)}
                    className="text-sm text-[#0F172A] hover:text-gray-500 transition-colors cursor-pointer w-full text-center focus:outline-none"
                >
                    {formatDate(startDate) || '12/12'}
                </button>
                {showStartPicker && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 z-50">
                        {renderCalendar(handleStartDateChange)}
                    </div>
                )}
            </div>

            {/* Separador "-" */}
            <span className="text-sm font-normal text-[#0F172A] whitespace-nowrap">
                -
            </span>

            {/* Campo de fecha final */}
            <div
                className="relative flex-1"
                ref={endPickerRef}
            >
                <button
                    onClick={() => setShowEndPicker(!showEndPicker)}
                    className="text-sm text-[#0F172A] hover:text-gray-500 transition-colors cursor-pointer w-full text-center focus:outline-none"
                >
                    {formatDate(endDate) || '24/12'}
                </button>
                {showEndPicker && (
                    <div className="absolute top-full translate-x-[-70%] mt-4 z-50">
                        {renderCalendar(handleEndDateChange)}
                    </div>
                )}
            </div>
        </div>
    )
}
